import { useState, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { Box, Typography, Snackbar, Alert } from '@mui/material'
import { format } from 'date-fns'

import useTranslations from '../../../i18n/useTranslations'
import { ReactComponent as RocketLaunch } from '../../../assets/icons/rocket-launch.svg'
import { ReactComponent as Clock } from '../../../assets/icons/clock.svg'
import ContactAndShippingInfo from '../../../ui/views/ShippingSendMobile/ContactAndShippingInfo'
import FormActions from '../../../ui/views/ShippingSendMobile/FormActions'
import * as routes from '../../../routes'
import PickupDate from '../../../ui/views/ShippingSendMobile/PickupDateMobile/View'
import ChoosePickup from '../../../context/ShippingSend/Step1Origin/Pickup/ChoosePickup'
import Asap from '../../../context/ShippingSend/Step1Origin/Pickup/Asap'
import { useAuthedFetch } from '../../../auth/useAuthedFetch'

import useShippingSendMobileForm from './useShippingSendMobileForm'
import useShippingSendMobile from './useShippingSendMobile'
import useShippingSendActionsLayout from './useShippingSendActionsLayout'
import OriginSection from './OriginSection'
import DestinationSection from './DestinationSection'
import useAvailableHours from '../../ShippingSend/Step1Origin/useAvailableHours'
import {
  calculateSchedule,
  calculateScheduleOptions,
  calculateTimezoneOptionsByDay,
} from '../../ShippingSend/Step1Origin'
import useFixedPickup, {
  parseCentersShippingAddress,
} from '../../ShippingSend/Step1Origin/useFixedPickup'

const createPickupDateId = ({ minDate, maxDate }) => {
  return `${minDate}/${maxDate}`
}

const BACKEND_ENDPOINT = process.env.REACT_APP_BACKEND_ENDPOINT

const SNACKBAR_AUTO_HIDE_DURATION = 3000

const Form = ({ userData }) => {
  const authedFetch = useAuthedFetch()
  const formatMessage = useTranslations()
  const [asapAlert, setAsapAlert] = useState(true)
  const [chooseDayAndHourAlert, setChooseDayAndHourAlert] = useState(false)

  const { centres: userCenters, defaultCentreCode, auditData } = userData
  parseCentersShippingAddress(userCenters)

  const history = useHistory()
  const formikSubmitBtnRef = useRef()

  const [serverErrors, setServerErrors] = useState()
  const [isAddressConfirmed, setIsAddressConfirmed] = useState(false)

  const { submitForm, isSubmitting } = useShippingSendMobile(
    setServerErrors,
    auditData,
  )

  const [newShippingCode, setNewShippingCode] = useState(undefined)

  const {
    serviceTypeOptions,
    values,
    touched,
    handleBlur,
    handleSubmit,
    fieldErrors,
    formErrors,
    handleChangeValue,
  } = useShippingSendMobileForm(
    defaultCentreCode,
    userCenters,
    submitForm,
    serverErrors,
    shippingCode => {
      setNewShippingCode(shippingCode)
    },
  )

  // check if current address matchs with fixed pickups addresses
  const { firstFixedPickupTimeZone } = useFixedPickup(
    userCenters,
    values.centerCode,
    {
      address: values.origin.address,
      city: values.origin.postalCode?.city,
      country: values.origin.country,
      postalCode: values.origin.postalCode?.postalCode,
    },
  )

  const availableHoursQuery = useAvailableHours(
    values.origin.postalCode.postalCode,
    format(new Date(), 'dd-MM-yyyy'),
  )

  const enabledCalendarDays = useMemo(
    () =>
      !Array.isArray(availableHoursQuery.data)
        ? []
        : availableHoursQuery.data.map(pickupDay =>
            format(pickupDay[0].minDate, 'yyyy-MM-dd'),
          ),
    [availableHoursQuery.data],
  )

  const schedules =
    availableHoursQuery?.data && values.pickupDay !== ''
      ? calculateSchedule(values.pickupDay, availableHoursQuery.data)
      : []
  const scheduleOptions = calculateScheduleOptions(schedules)

  const timezoneOptions =
    Array.isArray(availableHoursQuery.data) && values.pickupDay !== ''
      ? calculateTimezoneOptionsByDay(
          values.pickupDay,
          availableHoursQuery.data,
        )
      : []

  const showSubmitButton =
    Object.entries(fieldErrors)?.length === 0 && isAddressConfirmed
  const showSendError = Boolean(formErrors?.length > 0)
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)

  const snackbarBottomPosition = useShippingSendActionsLayout(
    showSubmitButton,
    showSendError,
    showCancelConfirmation,
  )

  const clickSubmitButton = () => {
    setServerErrors()
    formikSubmitBtnRef.current.click()
  }

  const goToHome = () => {
    history.push(routes.HOME.linkTo())
  }

  const handleCancelSend = () => {
    goToHome()
  }

  const PICKUP_OPTIONS = [
    {
      value: 'asap',
      label: formatMessage(
        !!firstFixedPickupTimeZone ? 'message:fixedPickup' : 'message:asap',
      ),
      icon: <RocketLaunch />,
      action: () => {
        setAsapAlert(true)
        setChooseDayAndHourAlert(false)
      },
    },
    {
      value: 'chooseDayAndHour',
      label: formatMessage('action:want-choose-day-time'),
      icon: <Clock />,
      action: () => {
        setAsapAlert(false)
        setChooseDayAndHourAlert(true)
      },
    },
  ]

  const [firstPickupDate, setFirstPickupDate] = useState(null)

  useEffect(() => {
    const setFixedPickupDates = () => {
      const pickupDateId = createPickupDateId({
        minDate: firstFixedPickupTimeZone.fromDate,
        maxDate: firstFixedPickupTimeZone.toDate,
      })
      if (values.pickup !== pickupDateId) {
        handleChangeValue({ name: 'pickup', value: pickupDateId })
        handleChangeValue({ name: 'fixedPickup', value: true })
        setFirstPickupDate(pickupDateId)
      }
    }

    const setAsapDates = () => {
      const firstPickupDay = availableHoursQuery?.data?.at(0)
      const minDateTimeSlot = firstPickupDay?.at(0).minDate
      const maxDateTimeSlot = firstPickupDay?.at(
        firstPickupDay?.length - 1,
      ).maxDate
      const pickupDateId = createPickupDateId({
        minDate: minDateTimeSlot,
        maxDate: maxDateTimeSlot,
      })
      handleChangeValue({ name: 'pickup', value: pickupDateId })
      handleChangeValue({ name: 'fixedPickup', value: false })
      setFirstPickupDate(pickupDateId)
    }

    if (
      asapAlert &&
      availableHoursQuery.isFetched &&
      !availableHoursQuery.isError
    ) {
      if (firstFixedPickupTimeZone) {
        setFixedPickupDates()
      } else {
        setAsapDates()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableHoursQuery.isFetched, firstFixedPickupTimeZone, asapAlert])

  const handleSubmitForm = e => {
    e.preventDefault()
    handleSubmit(e)
  }

  const [snackbar, setSnackbar] = useState({
    text: '',
    severity: 'info',
  })

  const closeSnackbar = () => {
    setSnackbar(snackbar => ({ ...snackbar, text: '' }))
  }

  const openSnackbar = (i18nKey, severity = 'info') => {
    setSnackbar({ text: formatMessage(i18nKey), severity })
  }

  const saveAddressIntoAgenda = (address, source) => {
    if (address.saveAddress) {
      const payload = {
        address_type_code: source,
        client_center_code: values.centerCode,
        client_code: userData.clientCode,
        name: address.name,
        address: address.address,
        address_2: address.city,
        postal_code: address.postalCode.postalCode,
        country_code: address.country,
        email: address?.email ?? '',
        phone: address.phone,
        is_active: true,
      }

      const url = `${BACKEND_ENDPOINT}/client-centers/${values.centerCode}/client-addresses`

      authedFetch('POST', url, { data: payload })
        .then(() => {
          openSnackbar('shipping-send-form:snackbar-address-saved')
        })
        .catch(() => {
          openSnackbar('shipping-send-form:alert-address-saved-error', 'error')
        })
    }
  }

  return (
    <Box sx={{ mb: 20 }}>
      <Snackbar
        severity={snackbar.severity}
        open={snackbar.text !== ''}
        autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={() => {
          closeSnackbar()
        }}
      >
        <Alert
          onClose={() => {
            closeSnackbar()
          }}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.text}
        </Alert>
      </Snackbar>

      <OriginSection
        centerCode={values.centerCode}
        userCenters={userCenters}
        originAddress={values.origin}
        onChangeCenterCode={value =>
          handleChangeValue({ name: 'centerCode', value })
        }
        onConfirmAddress={address => {
          handleChangeValue({ name: 'origin', value: address })
          saveAddressIntoAgenda(address, 'ORIGIN')
        }}
      />

      <DestinationSection
        isAddressConfirmed={isAddressConfirmed}
        setIsAddressConfirmed={setIsAddressConfirmed}
        confirmedAddress={values.destination}
        centreCode={values.centerCode}
        onConfirmAddress={address => {
          handleChangeValue({ name: 'destination', value: address })
          saveAddressIntoAgenda(address, 'DESTIN')
        }}
      />
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h2"
          sx={{
            paddingLeft: 2,
          }}
        >
          {formatMessage('shipping-send-form:title:pick-up-date')}
        </Typography>

        <PickupDate
          timetableOptions={PICKUP_OPTIONS}
          defaultValue={PICKUP_OPTIONS[0].value}
        >
          {asapAlert && !firstFixedPickupTimeZone && (
            <Asap
              pickupDate={firstPickupDate}
              isError={availableHoursQuery.isError}
              isLoading={availableHoursQuery.isLoading}
            />
          )}

          {asapAlert && !!firstFixedPickupTimeZone && (
            <Asap pickupDate={firstPickupDate} />
          )}

          {chooseDayAndHourAlert && (
            <ChoosePickup
              changeDay={day => {
                handleChangeValue({ name: 'pickupDay', value: day })
              }}
              selectedDay={values.pickupDay}
              enabledCalendarDays={enabledCalendarDays}
              changeSchedule={schedule => {
                handleChangeValue({ name: 'pickupSchedule', value: schedule })
              }}
              selectedSchedule={values.pickupSchedule}
              scheduleOptions={scheduleOptions}
              timezoneOptions={timezoneOptions}
              selectedTimezone={values.pickupTimezone}
              pickupDate={values.pickup}
              changeTimezone={timezone => {
                handleChangeValue({ name: 'pickupTimezone', value: timezone })
              }}
              changePickupDate={pickup => {
                handleChangeValue({ name: 'pickup', value: pickup })
              }}
              firstPickupDate={firstPickupDate}
              hasFixedPickup={!!firstFixedPickupTimeZone}
            />
          )}
        </PickupDate>
      </Box>
      {/* NOTE: do not include DestinationSection inside form due to AddressEntry internal form */}
      <form noValidate onSubmit={handleSubmitForm}>
        <ContactAndShippingInfo
          serviceTypeOptions={serviceTypeOptions}
          values={values}
          fieldErrors={fieldErrors}
          touched={touched}
          onChangeValue={handleChangeValue}
          onBlur={handleBlur}
        />
        <button ref={formikSubmitBtnRef} type="submit" hidden />
      </form>
      <FormActions
        formErrors={formErrors}
        showSubmitButton={showSubmitButton}
        onCancelProcess={handleCancelSend}
        onSubmit={clickSubmitButton}
        snackbarBottomPosition={snackbarBottomPosition}
        showSendError={showSendError}
        showCancelConfirmation={showCancelConfirmation}
        setShowCancelConfirmation={setShowCancelConfirmation}
        isSubmitting={isSubmitting}
        newShippingCode={newShippingCode}
        goToHome={goToHome}
        requestAnotherShipping={() => {
          // NOTE: solución provisional. Lo ideal sería reiniciar el estado de
          // este componente, junto con los estados de los formularios de formik
          // y las agendas.
          // TODO: cuando se pueda, investigar la manera de hacer lo comentado.
          window.location.reload()
        }}
      />
    </Box>
  )
}

Form.propTypes = {
  userData: PropTypes.object,
}

export default Form
